import { handleActions } from "redux-actions";
import {
  defaultStep,
  getPriceRange,
  calculateHasSelectedValuesState
} from "./priceRangeSelectors";
import { hasFacets } from "./selectors";
import { getNewRangeState } from "./priceRangeReducerHelper";
import {
  REQUEST_SEARCH,
  RECEIVE_SEARCH,
  UPDATE_MULTI_FACET,
  UPDATE_RANGE_FACET,
  CLEAR_RANGE_FACET,
  CLEAR_SELECTIONS,
  SET_RANGE_VALUES,
  MORE_PRODUCTS_LOADED,
  PREVIOUS_PRODUCTS_LOADED,
  LOADING_MORE_PRODUCTS,
  LOADING_PREVIOUS_PRODUCTS,
  UNDO_RANGE_VALUES,
  UPDATE_SORT_FACET,
  SET_NEW_SEARCH,
  CLEAR_NEW_SEARCH
} from "./constants";
import { mapProducts } from "./product";
import config from "./../../../config/common";
import {
  extendOffsetLowerBound,
  extendOffsetUpperBound,
  getOffsetFromQuery
} from "./offset";
import productTileReducer from "./productTile/reducer";
import getPreviousPageNumber from "./getPreviousPageNumber";
import getNextPageNumber from "./getNextPageNumber";

const pageSize = config.api.product.search.pageSize;

export {
  fetchSearch,
  fetchCategory,
  changeMultiFacet,
  changeMultiFacetAndApplyIfLargeMedium,
  changeRangeFacet,
  applyRangeFacet,
  applyFacets,
  clearAll,
  selectAll,
  clear,
  clearSelections,
  retrySearch,
  done
} from "./actions";

export {
  getPages,
  hasFacets,
  getFacets,
  getItemCount,
  getFacetSelectedValues,
  getFacetTotalSelected,
  getSearchTerm,
  isExactMatch,
  getSuggestTerms
} from "./selectors";
export { getStateForPriceSlider } from "./priceRangeSelectors";

export const initialState = {
  searchTerm: "",
  redirectUrl: "",
  products: [],
  facets: [],
  selectedFacets: [],
  selectedSort: {},
  error: null,
  isLoading: false,
  isNewSearch: true,
  itemCount: 0,
  offset: {
    initial: 0,
    lower: 0,
    upper: pageSize
  },
  isLoadingMore: false,
  isLoadingPrevious: false,
  showLoadMore: false,
  tileToFocus: {
    first: false,
    last: false,
    pageNumber: null
  },
  range: {
    currentprice: {
      selected: {
        current: null,
        temp: null
      },
      min: 0,
      max: 0,
      step: defaultStep,
      hasSelectedValues: false
    }
  },
  diagnostics: {
    recommendationsAnalytics: {}
  },
  searchPassMeta: {
    alternateSearchTerms: []
  }
};

const shouldShowLoadMore = (offset, itemCount) => offset.upper < itemCount;

const shouldShowLoadPrevious = offset => offset.lower > 0;

const hasFilterReturnedNoResults = (newItemCount, previousState) =>
  newItemCount === 0 && hasFacets({ search: previousState });

const filterCurrentPrice = selectedFacets =>
  selectedFacets.filter(filter => filter.facetId === "currentprice");

export default handleActions(
  {
    ...productTileReducer,
    [LOADING_PREVIOUS_PRODUCTS]: state => ({
      ...state,
      isLoadingPrevious: true,
      isNewSearch: false
    }),
    [PREVIOUS_PRODUCTS_LOADED]: (state, { error, payload }) => {
      if (error) {
        return {
          ...state,
          isLoadingPrevious: false,
          tileToFocus: {
            ...initialState.tileToFocus
          }
        };
      }

      const products = mapProducts(payload.products).concat(state.products);
      const offset = extendOffsetLowerBound(state, payload);

      return {
        ...state,
        offset,
        tileToFocus: {
          first: false,
          last: true,
          pageNumber: getPreviousPageNumber(state)
        },
        products,
        isLoadingPrevious: false,
        showLoadPrevious: shouldShowLoadPrevious(offset),
        error: null
      };
    },
    [LOADING_MORE_PRODUCTS]: state => ({
      ...state,
      isLoadingMore: true,
      isNewSearch: false
    }),
    [MORE_PRODUCTS_LOADED]: (state, { error, payload }) => {
      if (error) {
        return {
          ...state,
          isLoadingMore: false,
          tileToFocus: {
            ...initialState.tileToFocus
          }
        };
      }

      const products = state.products.concat(mapProducts(payload.products));
      const offset = extendOffsetUpperBound(state, payload);

      return {
        ...state,
        offset,
        tileToFocus: {
          first: true,
          last: false,
          pageNumber: getNextPageNumber(state)
        },
        products,
        isLoadingMore: false,
        showLoadMore: shouldShowLoadMore(offset, payload.itemCount),
        error: null
      };
    },
    [REQUEST_SEARCH]: (state, { payload }) => ({
      ...state,
      query: payload,
      isLoading: true,
      isNewSearch: true
    }),
    [RECEIVE_SEARCH]: (state, { error, payload }) => {
      if (error) {
        return {
          ...state,
          isLoading: SERVER,
          tileToFocus: {
            ...initialState.tileToFocus
          }
        };
      }

      const {
        products,
        itemCount,
        diagnostics,
        searchPassMeta,
        searchTerm,
        redirectUrl,
        query = {}
      } = payload;

      const facets = Array.isArray(payload.facets) ? [...payload.facets] : [];

      const preserveFacetsForFilterOut = hasFilterReturnedNoResults(
        itemCount,
        state
      )
        ? {
            facets: state.facets,
            range: state.range,
            selectedFacets: filterCurrentPrice(state.oldSelectedFacets)
          }
        : {};

      const offset = getOffsetFromQuery(state, payload);

      return {
        ...state,
        facets,
        range: getNewRangeState(initialState, state, facets, query),
        products: mapProducts(products),
        isLoading: false,
        redirectUrl,
        selectedFacets: initialState.selectedFacets,
        itemCount,
        offset,
        tileToFocus: {
          ...initialState.tileToFocus
        },
        showLoadMore: shouldShowLoadMore(offset, itemCount),
        showLoadPrevious: shouldShowLoadPrevious(offset),
        diagnostics,
        searchPassMeta,
        searchTerm,
        error: null,
        oldSelectedFacets: [],
        ...preserveFacetsForFilterOut
      };
    },
    [UPDATE_MULTI_FACET]: (state, { payload }) => ({
      ...state,
      selectedFacets: state.selectedFacets.concat(payload)
    }),
    [UPDATE_RANGE_FACET]: (state, { payload }) => ({
      ...state,
      range: {
        currentprice: {
          ...state.range.currentprice,
          selected: {
            ...state.range.currentprice.selected,
            temp: payload.value
          },
          hasSelectedValues: calculateHasSelectedValuesState(
            payload.value,
            state.range.currentprice
          )
        }
      },
      selectedFacets: state.selectedFacets.concat(
        getPriceRange(state).map(facet => ({
          facetId: facet.id,
          isSelected: true,
          valueId: facet.facetValues[0].id
        }))
      )
    }),
    [UPDATE_SORT_FACET]: (state, { payload }) => ({
      ...state,
      selectedSort: { value: payload }
    }),
    [CLEAR_RANGE_FACET]: state => ({
      ...state,
      range: {
        currentprice: {
          ...state.range.currentprice,
          selected: {
            ...state.range.currentprice.selected,
            temp: {
              leftIndicator: state.range.currentprice.min,
              rightIndicator: state.range.currentprice.max
            }
          },
          hasSelectedValues: false
        }
      },
      selectedFacets: state.selectedFacets.concat(
        getPriceRange(state).map(facet => ({
          facetId: facet.id,
          isSelected: false,
          valueId: facet.facetValues[0].id
        }))
      )
    }),
    [CLEAR_SELECTIONS]: state => ({
      ...state,
      selectedSort: {},
      selectedFacets: hasFilterReturnedNoResults(state.itemCount, state)
        ? filterCurrentPrice(state.selectedFacets)
        : initialState.selectedFacets,
      range: hasFilterReturnedNoResults(state.itemCount, state)
        ? state.range
        : {
            currentprice: {
              ...state.range.currentprice,
              selected: {
                ...state.range.currentprice.selected,
                temp: null
              },
              hasSelectedValues: calculateHasSelectedValuesState(
                state.range.currentprice.selected.current,
                state.range.currentprice
              )
            }
          }
    }),
    [SET_RANGE_VALUES]: (state, { payload }) => ({
      ...state,
      selectedFacets: initialState.selectedFacets,
      oldSelectedFacets: state.selectedFacets,
      range: {
        currentprice: {
          ...state.range.currentprice,
          selected: {
            previous: state.range.currentprice.selected.current,
            current: payload.values,
            temp: null
          },
          hasSelectedValues: calculateHasSelectedValuesState(
            payload.values,
            state.range.currentprice
          )
        }
      }
    }),
    [UNDO_RANGE_VALUES]: state => {
      if (state.range.currentprice.selected.previous) {
        return {
          ...state,
          range: {
            currentprice: {
              ...state.range.currentprice,
              selected: {
                current: state.range.currentprice.selected.previous,
                previous: null
              }
            }
          }
        };
      }

      return state;
    },
    [SET_NEW_SEARCH]: state => ({
      ...state,
      isNewSearch: true
    }),
    [CLEAR_NEW_SEARCH]: state => ({
      ...state,
      isNewSearch: false
    })
  },
  initialState
);
